import React, { useState, useMemo, useEffect, useRef } from 'react';
import apiClient from '../api/apiClient';
import Swal from 'sweetalert2';
import '../styles/datatable.css';

/* Columns whose full text should appear in a popover on hover */
const hoverPreviewColumns = ['content', 'description', 'text_sentiment', 'word_sentiment'];

/* Popover component — anchors to the hovered cell */
const CellPopover = ({ text, anchorRect }) => {
  if (!text || !anchorRect) return null;
  const displayText = typeof text === 'object' ? JSON.stringify(text, null, 2) : String(text);
  if (displayText.length < 60) return null;

  return (
    <div className="dt__popover" style={{
      top: anchorRect.bottom + 6,
      left: Math.min(anchorRect.left, window.innerWidth - 380),
    }}>
      <div className="dt__popover-content">{displayText}</div>
    </div>
  );
};

const DataTable = ({ data: initialData, endpoint, onDataChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(initialData || []);
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [foreignKeyData, setForeignKeyData] = useState({});
  const [foreignKeyLoading, setForeignKeyLoading] = useState(false);
  const [foreignKeyErrors, setForeignKeyErrors] = useState({});
  const [displayColumns, setDisplayColumns] = useState([]);
  const [popover, setPopover] = useState({ text: null, rect: null });
  const popoverTimeout = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pageSizeOptions = [10, 30, 50, { value: 'all', label: 'All' }];

  const booleanColumns = ['is_active', 'is_test', 'is_processed', 'is_deleted'];
  const readOnlyColumns = ['id', 'file_uuid', 'created_at', 'updated_at', 'token', 'reset_password'];
  const hiddenColumns = useMemo(() => ['id', 'file_uuid', 'token', 'reset_password'], []);
  const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

  const isArticles = endpoint === 'articles';

  const displayBooleanValue = (value) => {
    if (value === true || value === 1 || value === '1') return 'Yes';
    if (value === false || value === 0 || value === '0') return 'No';
    return value;
  };

  const parseBooleanInput = (value) => {
    if (value === 'Yes') return 1;
    if (value === 'No') return 0;
    return value;
  };

  const getForeignKeyName = (column, id) => {
    if (!foreignKeyData[column] || !id) return id;
    const relatedItem = foreignKeyData[column].find(item => item.id === id);
    return relatedItem ? (relatedItem.name || relatedItem.title || relatedItem.label || id) : id;
  };

  useEffect(() => {
    setData(initialData || []);
    setCurrentPage(1);
  }, [initialData]);

  useEffect(() => {
    if (data.length > 0) {
      const allColumns = Object.keys(data[0]);
      setDisplayColumns(allColumns.filter(col => !hiddenColumns.includes(col)));
    }
  }, [data, hiddenColumns]);

  useEffect(() => {
    if (data.length === 0) return;
    const fkColumns = Object.keys(data[0])
      .filter(col => col.endsWith('_id') && col !== 'id')
      .filter(col => !(col in foreignKeyData));
    if (fkColumns.length === 0) return;

    const fetchForeignKeyData = async () => {
      setForeignKeyLoading(true);
      const newFkData = {};
      const newErrors = {};

      for (const col of fkColumns) {
        let relatedEndpoint = col.replace(/_id$/, '');
        if (relatedEndpoint === 'address') relatedEndpoint = 'addresses';
        else if (relatedEndpoint.endsWith('y')) relatedEndpoint = relatedEndpoint.slice(0, -1) + 'ies';
        else relatedEndpoint = relatedEndpoint + 's';

        try {
          const response = await apiClient.get(`api/${relatedEndpoint}/v1`);
          newFkData[col] = response.data;
        } catch (error) {
          console.error(`Error fetching data for ${relatedEndpoint}:`, error);
          newErrors[col] = `Could not load ${relatedEndpoint}`;
          newFkData[col] = [];
        }
      }

      setForeignKeyData(prev => ({ ...prev, ...newFkData }));
      setForeignKeyErrors(prev => ({ ...prev, ...newErrors }));
      setForeignKeyLoading(false);
    };

    fetchForeignKeyData();
  }, [data, foreignKeyData]);

  const filteredData = data.filter((item) =>
    Object.entries(item).some(([key, value]) => {
      if (key.endsWith('_id') && foreignKeyData[key]) {
        const name = getForeignKeyName(key, value);
        return name?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      }
      return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
    })
  );

  const indexOfLastItem = itemsPerPage === 'all' ? filteredData.length : currentPage * itemsPerPage;
  const indexOfFirstItem = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const currentItems = itemsPerPage === 'all' ? filteredData : filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    const editData = { ...item };
    booleanColumns.forEach(col => {
      if (col in editData) editData[col] = displayBooleanValue(editData[col]);
    });
    setFormData(editData);
  };

  const handleCancel = () => { setEditingId(null); setIsAdding(false); };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let dataToSend, response;

      if (editingId) {
        dataToSend = { ...formData };
        booleanColumns.forEach(col => { if (col in dataToSend) dataToSend[col] = parseBooleanInput(dataToSend[col]); });
        response = await apiClient.put(`api/${endpoint}/v1/${editingId}`, dataToSend);
        setData(data.map(item => item.id === editingId ? response.data : item));
        onDataChange?.();
      } else if (isAdding) {
        dataToSend = { ...newItem };
        booleanColumns.forEach(col => { if (col in dataToSend) dataToSend[col] = parseBooleanInput(dataToSend[col]); });
        readOnlyColumns.forEach(col => { delete dataToSend[col]; });
        response = await apiClient.post(`api/${endpoint}/v1`, dataToSend);
        setData([...data, response.data]);
        onDataChange?.();
      }

      if (response?.data?.message) {
        await Swal.fire({ title: 'Success', text: response.data.message, icon: 'success', confirmButtonText: 'OK', confirmButtonColor: '#c9453b' });
        try {
          const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
          setData(refreshResponse.data);
          onDataChange?.();
        } catch (e) { console.error('Error refreshing:', e); }
      }
    } catch (error) {
      console.error('Error saving data:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'An error occurred while saving the data',
        icon: 'error', confirmButtonText: 'OK', confirmButtonColor: '#c9453b'
      });
    } finally {
      setIsLoading(false);
      setEditingId(null);
      setIsAdding(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this item?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#c9453b',
    });

    if (!result.isConfirmed) return;

    setIsLoading(true);
    try {
      const response = await apiClient.delete(`api/${endpoint}/v1/${id}`);
      setData(data.filter(item => item.id !== id));
      onDataChange?.();

      if (response?.data?.message) {
        await Swal.fire({ title: 'Deleted', text: response.data.message, icon: 'success', confirmButtonText: 'OK', confirmButtonColor: '#c9453b' });
        try {
          const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
          setData(refreshResponse.data);
          onDataChange?.();
        } catch (e) { console.error('Error refreshing:', e); }
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      if (error.response?.data?.message) {
        await Swal.fire({ title: 'Error', text: error.response.data.message, icon: 'error', confirmButtonText: 'OK', confirmButtonColor: '#c9453b' });
      }
    } finally { setIsLoading(false); }
  };

  /* ── Article quick-actions: toggle is_deleted / is_processed ── */
  const handleToggleField = async (item, field) => {
    const currentVal = item[field];
    const isCurrentlySet = currentVal === true || currentVal === 1 || currentVal === '1';
    const newVal = isCurrentlySet ? 0 : 1;

    const label = field === 'is_deleted' ? 'deleted' : 'processed';
    const action = isCurrentlySet
      ? `Remove "${label}" flag from this article?`
      : `Mark this article as ${label}?`;

    const confirmResult = await Swal.fire({
      title: action,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#c9453b',
    });

    if (!confirmResult.isConfirmed) return;

    setIsLoading(true);
    try {
      const payload = { ...item, [field]: newVal };
      await apiClient.put(`api/${endpoint}/v1/${item.id}`, payload);

      const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
      setData(refreshResponse.data);
      onDataChange?.();

      await Swal.fire({
        title: 'Updated',
        text: `Article marked as ${newVal ? label : 'not ' + label}.`,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#c9453b',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(`Error toggling ${field}:`, error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || `Failed to update ${label} status.`,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#c9453b',
      });
    } finally { setIsLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (editingId) setFormData({ ...formData, [name]: value });
    else if (isAdding) setNewItem({ ...newItem, [name]: value });
  };

  const handleAddNew = () => {
    setIsAdding(true);
    if (data.length > 0) {
      const emptyItem = {};
      Object.keys(data[0]).forEach(key => {
        emptyItem[key] = booleanColumns.includes(key) ? 'No' : '';
        if (readOnlyColumns.includes(key)) delete emptyItem[key];
      });
      setNewItem(emptyItem);
    }
  };

  /* ── Hover popover helpers ── */
  const showPopover = (text, e) => {
    clearTimeout(popoverTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setPopover({ text, rect });
  };

  const hidePopover = () => {
    popoverTimeout.current = setTimeout(() => setPopover({ text: null, rect: null }), 150);
  };

  const renderFormField = (col, value, onChange) => {
    if (booleanColumns.includes(col)) {
      return (
        <select name={col} value={value || 'No'} onChange={onChange} className="dt-select">
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      );
    }
    if (col === 'day_of_week') {
      return (
        <select name={col} value={value || ''} onChange={onChange} className="dt-select">
          <option value="">Select Day</option>
          {weekdays.map(day => <option key={day} value={day}>{day}</option>)}
        </select>
      );
    }
    if (col.endsWith('_id') && foreignKeyData[col]) {
      if (foreignKeyErrors[col]) return <span className="dt-error-inline">{foreignKeyErrors[col]}</span>;
      return (
        <select name={col} value={value || ''} onChange={onChange} disabled={foreignKeyLoading} className="dt-select">
          <option value="">Select {col.replace('_id', '').replace(/_/g, ' ')}</option>
          {foreignKeyData[col].map(item => (
            <option key={item.id} value={item.id}>{item.name || item.title || item.label || item.id}</option>
          ))}
        </select>
      );
    }
    return (
      <input type="text" name={col} value={value || ''} onChange={onChange}
        disabled={col === 'id' || readOnlyColumns.includes(col)} className="dt-input" />
    );
  };

  /* ── Render cell value, with hover support for content-like columns ── */
  const renderCellValue = (item, col) => {
    const raw = item[col];
    const isPreviewCol = hoverPreviewColumns.includes(col);
    const displayText = typeof raw === 'object' ? JSON.stringify(raw) : raw;

    if (booleanColumns.includes(col)) {
      return (
        <span className={`dt__badge ${displayBooleanValue(raw) === 'Yes' ? 'dt__badge--yes' : 'dt__badge--no'}`}>
          {displayBooleanValue(raw)}
        </span>
      );
    }
    if (col.endsWith('_id')) {
      return getForeignKeyName(col, raw);
    }

    if (isPreviewCol && displayText && String(displayText).length > 50) {
      return (
        <span
          className="dt__cell-value dt__cell-value--hoverable"
          onMouseEnter={(e) => showPopover(raw, e)}
          onMouseLeave={hidePopover}
        >
          {String(displayText).substring(0, 50)}…
        </span>
      );
    }

    return <span className="dt__cell-value">{displayText}</span>;
  };

  if (!data || data.length === 0) {
    return (
      <div className="dt">
        <div className="dt__toolbar">
          <button className="btn btn--primary btn--sm" onClick={handleAddNew}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            Add New
          </button>
        </div>
        <div className="dt__empty">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none"><rect x="6" y="10" width="36" height="28" rx="3" stroke="var(--ink-muted)" strokeWidth="1.5"/><path d="M6 18h36M18 18v20" stroke="var(--ink-muted)" strokeWidth="1.5"/></svg>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const allColumns = Object.keys(data[0]).filter(col =>
    !(isAdding && readOnlyColumns.includes(col)) && !hiddenColumns.includes(col)
  );

  return (
    <div className="dt">
      {/* Toolbar */}
      <div className="dt__toolbar">
        <div className="dt__search">
          <svg className="dt__search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input type="text" placeholder="Search records…" value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="dt__search-input" />
        </div>
        <button className="btn btn--primary btn--sm" onClick={handleAddNew} disabled={isAdding}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          Add New
        </button>
      </div>

      {/* Pagination top */}
      <div className="dt__pagination">
        <div className="dt__per-page">
          <label>Show</label>
          <select value={itemsPerPage} onChange={handleItemsPerPageChange} disabled={isLoading} className="dt-select dt-select--sm">
            {pageSizeOptions.map(option => (
              <option key={option.value || option} value={option.value || option}>{option.label || option}</option>
            ))}
          </select>
          <span>entries</span>
        </div>
        <span className="dt__count">{filteredData.length} record{filteredData.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Add form */}
      {isAdding && (
        <div className="dt__form-card">
          <h3 className="dt__form-title">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 1v16M1 9h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            New Record
          </h3>
          <div className="dt__form-grid">
            {allColumns.map((col) => (
              <div key={`new-${col}`} className="dt__form-field">
                <label className="dt__form-label">{col.replace(/_/g, ' ')}</label>
                {renderFormField(col, newItem[col], handleChange)}
              </div>
            ))}
          </div>
          <div className="dt__form-actions">
            <button onClick={handleSave} disabled={isLoading} className="btn btn--primary btn--sm">
              {isLoading ? 'Saving…' : 'Save'}
            </button>
            <button onClick={handleCancel} disabled={isLoading} className="btn btn--ghost btn--sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Table — scrollable */}
      <div className="dt__table-wrap">
        <table className="dt__table">
          <thead>
            <tr>
              {displayColumns.map((col) => (
                <th key={col}>
                  {col.endsWith('_id') ? col.replace('_id', '').replace(/_/g, ' ') : col.replace(/_/g, ' ')}
                </th>
              ))}
              <th className="dt__th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item) => (
              <tr key={item.id} className={editingId === item.id ? 'dt__row--editing' : ''}>
                {displayColumns.map((col) => (
                  <td key={`${item.id}-${col}`} data-label={col.replace(/_/g, ' ')}>
                    {editingId === item.id ? (
                      readOnlyColumns.includes(col) ? (
                        <span className="dt__readonly">{item[col]}</span>
                      ) : renderFormField(col, formData[col], handleChange)
                    ) : (
                      renderCellValue(item, col)
                    )}
                  </td>
                ))}
                <td className="dt__td-actions" data-label="Actions">
                  {editingId === item.id ? (
                    <div className="dt__action-group">
                      <button onClick={handleSave} disabled={isLoading} className="btn btn--primary btn--xs">
                        {isLoading ? '…' : 'Save'}
                      </button>
                      <button onClick={handleCancel} disabled={isLoading} className="btn btn--ghost btn--xs">Cancel</button>
                    </div>
                  ) : (
                    <div className="dt__action-group">
                      <button onClick={() => handleEdit(item)} disabled={isLoading || isAdding} className="btn btn--outline btn--xs" title="Edit">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M10.5 1.5l2 2-8 8H2.5v-2l8-8z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>
                      </button>
                      <button onClick={() => handleDelete(item.id)} disabled={isLoading || isAdding} className="btn btn--danger-ghost btn--xs" title="Delete">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 4h10M5 4V2.5h4V4M3.5 4v8h7V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>

                      {/* Article-specific quick actions */}
                      {isArticles && 'is_deleted' in item && (
                        <button
                          onClick={() => handleToggleField(item, 'is_deleted')}
                          disabled={isLoading || isAdding}
                          className={`btn btn--xs ${
                            (item.is_deleted === 1 || item.is_deleted === true || item.is_deleted === '1')
                              ? 'btn--warning-active' : 'btn--warning-ghost'
                          }`}
                          title={
                            (item.is_deleted === 1 || item.is_deleted === true || item.is_deleted === '1')
                              ? 'Unmark as deleted' : 'Mark as deleted'
                          }
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2 4h10M5 4V2.5h4V4M3.5 4v8h7V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            {(item.is_deleted === 1 || item.is_deleted === true || item.is_deleted === '1') &&
                              <path d="M2 12L12 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            }
                          </svg>
                          <span className="btn__label-sm">
                            {(item.is_deleted === 1 || item.is_deleted === true || item.is_deleted === '1') ? 'Undelete' : 'Delete'}
                          </span>
                        </button>
                      )}

                      {isArticles && 'is_processed' in item && (
                        <button
                          onClick={() => handleToggleField(item, 'is_processed')}
                          disabled={isLoading || isAdding}
                          className={`btn btn--xs ${
                            (item.is_processed === 1 || item.is_processed === true || item.is_processed === '1')
                              ? 'btn--success-active' : 'btn--success-ghost'
                          }`}
                          title={
                            (item.is_processed === 1 || item.is_processed === true || item.is_processed === '1')
                              ? 'Mark as unprocessed' : 'Mark as processed'
                          }
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M3 7.5l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span className="btn__label-sm">
                            {(item.is_processed === 1 || item.is_processed === true || item.is_processed === '1') ? 'Unprocess' : 'Process'}
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Content popover */}
      {popover.text && <CellPopover text={popover.text} anchorRect={popover.rect} />}

      {/* Pagination bottom */}
      {itemsPerPage !== 'all' && totalPages > 1 && (
        <div className="dt__pagination dt__pagination--bottom">
          <span className="dt__page-info">
            Page {currentPage} of {totalPages}
          </span>
          <div className="dt__page-btns">
            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1 || isLoading} className="btn btn--ghost btn--xs">First</button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className="btn btn--ghost btn--xs">Prev</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page;
              if (totalPages <= 5) page = i + 1;
              else if (currentPage <= 3) page = i + 1;
              else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
              else page = currentPage - 2 + i;
              return (
                <button key={page} onClick={() => handlePageChange(page)}
                  className={`btn btn--xs ${page === currentPage ? 'btn--primary' : 'btn--ghost'}`}
                  disabled={isLoading}>
                  {page}
                </button>
              );
            })}
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading} className="btn btn--ghost btn--xs">Next</button>
            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || isLoading} className="btn btn--ghost btn--xs">Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
